import { Head, Link, usePage } from "@inertiajs/react";
import { Trash2, UserPlus } from "lucide-react";
import { useEffect } from "react";

import { DataTablePagination } from "@/components/data-table-pagination";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useDataTable } from "@/hooks/use-data-table";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import { useSyncedDebouncedValue } from "@/hooks/use-synced-debounced-value";
import AppLayout from "@/layouts/app-layout";
import type { BreadcrumbItem, Organization, PagyMetadata, RansackFilters, SharedData } from "@/types";

import ImportMembersDialog from "./import-dialog";

interface MemberSummary {
  id: number;
  email: string;
  name?: string | null;
  full_name?: string | null;
  groups: string[];
}

interface MembersData {
  members: MemberSummary[];
  pagy: PagyMetadata;
}

interface MembersIndexProps {
  data: MembersData;
  filters: RansackFilters;
  organization: Organization;
}

function groupLabel(groupKey: string) {
  return groupKey.replace(/_/g, " ").toUpperCase();
}

export default function MembersIndex({ data, filters, organization }: MembersIndexProps) {
  const routes = useOrganizationRoutes();
  const { auth } = usePage<SharedData>().props;
  const canManage = auth.groups.includes("owner") || auth.groups.includes("admin");

  const { members, pagy } = data;

  const {
    isLoading,
    handleFilterChange,
    handlePageChange,
  } = useDataTable({
    filters,
    pagy,
    path: routes.members.index(),
  });

  const {
    value: searchValue,
    setValue: setSearchValue,
    debouncedValue: debouncedSearch,
  } = useSyncedDebouncedValue(
    filters.user_email_or_user_full_name_cont ?? "",
  );

  useEffect(() => {
    const normalized = debouncedSearch.trim();
    const nextValue = normalized ? normalized : undefined;

    if ((filters.user_email_or_user_full_name_cont ?? undefined) !== nextValue) {
      handleFilterChange({
        user_email_or_user_full_name_cont: nextValue,
      });
    }
  }, [debouncedSearch]);

  const clearFilters = () => {
    setSearchValue("");
    handleFilterChange({ user_email_or_user_full_name_cont: undefined });
  };

  const hasActiveSearch = Boolean(searchValue);

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Miembros",
      href: routes.members.index(),
    },
  ];

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Miembros" />
      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold">Miembros</h1>
            <p className="text-muted-foreground">
              Equipo de {organization.name}. Total: {pagy.count} miembros
            </p>
          </div>
          <div className="flex gap-2">
            {canManage && <ImportMembersDialog />}
            <Button asChild className="gap-2">
              <Link href={routes.members.new()}>
                <UserPlus className="h-4 w-4" />
                Agregar miembro
              </Link>
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="flex items-center gap-4">
          <Input
            placeholder="Buscar por nombre o email..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="max-w-sm"
          />
          {hasActiveSearch && (
            <Button variant="outline" onClick={clearFilters}>
              <Trash2 className="h-4 w-4" /> Limpiar filtros
            </Button>
          )}
        </div>

        {members.length === 0 ? (
          <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
            {hasActiveSearch
              ? "No se encontraron miembros con los filtros aplicados."
              : "Todavía no hay miembros en esta organización."}
          </div>
        ) : (
          <>
            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Roles</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell className="whitespace-normal break-words">
                        {member.full_name ?? member.name ?? "—"}
                      </TableCell>
                      <TableCell className="whitespace-normal break-words">
                        {member.email}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          {member.groups.map((group) => (
                            <Badge key={group} variant="regular" color="sky">
                              {groupLabel(group)}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" asChild>
                          <Link href={routes.members.show(member.id)}>
                            Ver detalle
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Server-side pagination */}
            <DataTablePagination
              pagy={pagy}
              isLoading={isLoading}
              onPageChange={handlePageChange}
              entityName="miembros"
            />
          </>
        )}
      </div>
    </AppLayout>
  );
}
